import type {
  CreateConversationResponse,
  SendMessageResponse,
  CompleteConversationResponse,
  UploadFileResponse,
  ContactInfo,
  ApiResponse,
  Attachment,
  ConversationMode,
  Message,
  LeaveMessageRequest,
  LeaveMessageResponse,
  AvailabilityData,
} from '@bitio-builder/shared';

interface ApiConfig {
  apiKey: string;
  apiUrl: string;
}

let config: ApiConfig | null = null;

export function initializeApi(apiKey: string, apiUrl: string) {
  config = { apiKey, apiUrl };
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  if (!config) {
    throw new Error('API not initialized');
  }

  const url = `${config.apiUrl}${endpoint}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-Key': config.apiKey,
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || { code: 'UNKNOWN', message: 'Unknown error' },
      };
    }

    return data as ApiResponse<T>;
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error',
      },
    };
  }
}

export interface UserIntro {
  name: string;
  email: string;
}

export async function createConversation(
  initialMessage?: string,
  language: 'sv' | 'en' = 'sv',
  mode: ConversationMode = 'claude',
  userIntro?: UserIntro
): Promise<ApiResponse<CreateConversationResponse>> {
  return fetchApi<CreateConversationResponse>('/conversations', {
    method: 'POST',
    body: JSON.stringify({
      initialMessage,
      mode,
      userIntro,
      metadata: {
        language,
        referrer: window.location.href,
        userAgent: navigator.userAgent,
      },
    }),
  });
}

export async function sendMessage(
  conversationId: string,
  content: string,
  attachments?: Attachment[]
): Promise<ApiResponse<SendMessageResponse>> {
  return fetchApi<SendMessageResponse>(`/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content, attachments }),
  });
}

export async function uploadFile(
  conversationId: string,
  file: File
): Promise<ApiResponse<UploadFileResponse>> {
  if (!config) {
    throw new Error('API not initialized');
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(
      `${config.apiUrl}/conversations/${conversationId}/images`,
      {
        method: 'POST',
        headers: {
          'X-API-Key': config.apiKey,
        },
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || { code: 'UNKNOWN', message: 'Upload failed' },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UPLOAD_ERROR',
        message: error instanceof Error ? error.message : 'Upload failed',
      },
    };
  }
}

export async function completeConversation(
  conversationId: string,
  contactInfo: ContactInfo
): Promise<ApiResponse<CompleteConversationResponse>> {
  return fetchApi<CompleteConversationResponse>(
    `/conversations/${conversationId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify({ contactInfo }),
    }
  );
}

// Poll for new messages (used in Slack mode)
export async function getMessages(
  conversationId: string,
  since?: string
): Promise<ApiResponse<{ messages: Message[] }>> {
  const params = since ? `?since=${encodeURIComponent(since)}` : '';
  return fetchApi<{ messages: Message[] }>(
    `/conversations/${conversationId}/messages${params}`,
    { method: 'GET' }
  );
}

// Submit a leave message (email contact form)
export async function submitLeaveMessage(
  data: LeaveMessageRequest
): Promise<ApiResponse<LeaveMessageResponse>> {
  return fetchApi<LeaveMessageResponse>('/leave-message', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Check team availability (office hours + Slack presence)
export async function fetchAvailability(): Promise<ApiResponse<AvailabilityData>> {
  return fetchApi<AvailabilityData>('/availability', {
    method: 'GET',
  });
}
